import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Quiet-luxury badges: tight spacing, uppercase tracking, hairline border,
 * subtle tint. The `dot` prop renders a colored dot prefix so consumers
 * don't have to wire it manually.
 *
 *   <Badge variant="success" dot>Online</Badge>
 *   <Badge variant="warning">Beta</Badge>
 *   <Badge variant="primary" size="lg">PRO</Badge>
 *
 * NOTA — em código novo prefira `Pill` (mais flexível: subtle/solid/glass +
 * dot pulse + icon prop). Badge segue vivo só pelos legacy variants
 * `eva-published` / `eva-draft` que rodam em ProjectCard / Editor header
 * sobre thumbnails. Ao tocar nesses lugares, migre para Pill `glass`.
 */

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'destructive'
    | 'outline'
    | 'eva-published'
    | 'eva-draft';
  size?: 'sm' | 'md' | 'lg';
  /** Renders a colored dot prefix that matches the variant. */
  dot?: boolean;
}

const variantStyles: Record<NonNullable<BadgeProps['variant']>, string> = {
  default:
    'bg-[var(--color-primary)]/12 text-[var(--color-primary)] border border-[var(--color-primary)]/25',
  primary:
    'bg-[var(--color-primary)]/12 text-[var(--color-primary)] border border-[var(--color-primary)]/25',
  secondary:
    'bg-[var(--color-accent)]/12 text-[var(--color-accent)] border border-[var(--color-accent)]/25',
  success:
    'bg-[var(--color-accent)]/12 text-[var(--color-accent)] border border-[var(--color-accent)]/25',
  warning:
    'bg-[var(--color-warning)]/12 text-[var(--color-warning)] border border-[var(--color-warning)]/30',
  destructive:
    'bg-[var(--color-destructive)]/12 text-[var(--color-destructive)] border border-[var(--color-destructive)]/30',
  outline:
    'bg-transparent text-[var(--color-text-muted)] border border-[var(--color-border)]',
  // EVA legacy aliases — DO NOT REMOVE (used in ProjectCard, Editor header)
  'eva-published':
    'bg-[var(--color-accent)]/20 text-[var(--color-accent)] border border-[var(--color-accent)]/30 backdrop-blur-md',
  'eva-draft':
    'bg-white/10 text-white border border-white/20 backdrop-blur-md',
};

const dotColor: Record<NonNullable<BadgeProps['variant']>, string> = {
  default: 'bg-[var(--color-primary)]',
  primary: 'bg-[var(--color-primary)]',
  secondary: 'bg-[var(--color-accent)]',
  success: 'bg-[var(--color-accent)]',
  warning: 'bg-[var(--color-warning)]',
  destructive: 'bg-[var(--color-destructive)]',
  outline: 'bg-[var(--color-text-muted)]',
  'eva-published': 'bg-[var(--color-accent)]',
  'eva-draft': 'bg-white',
};

const sizeStyles: Record<NonNullable<BadgeProps['size']>, string> = {
  sm: 'text-[10px] px-1.5 py-0.5 rounded-[6px] tracking-wider',
  md: 'text-[10px] px-2 py-1 rounded-[6px] tracking-wider',
  lg: 'text-xs px-3 py-1 rounded-[8px] tracking-wide',
};

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size = 'md', dot, children, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center gap-1.5 font-semibold uppercase whitespace-nowrap transition-colors',
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      {...props}
    >
      {dot && (
        <span
          className={cn('h-1.5 w-1.5 rounded-full', dotColor[variant])}
          aria-hidden
        />
      )}
      {children}
    </span>
  ),
);
Badge.displayName = 'Badge';

export { Badge };
